import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Upload, Users, Calendar, Swords, AlertTriangle, Pencil, Check, X, Trophy, Settings, Shield } from "lucide-react";
import { updateRaid } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import GuildSettingsDialog from "@/components/GuildSettingsDialog";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="stat-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" style={accent ? { color: accent } : {}} />
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <p className="text-2xl font-bold heading">{value ?? "—"}</p>
    </div>
  );
}

function RaidCard({ raid, onNavigate, onRenamed }) {
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState(raid.raid_name);

  const handleRename = async () => {
    if (!newName.trim() || newName === raid.raid_name) { setEditing(false); return; }
    try {
      await updateRaid(raid.id, { raid_name: newName.trim() });
      toast.success("Raid renamed");
      setEditing(false);
      if (onRenamed) onRenamed();
    } catch (e) {
      toast.error("Failed to rename");
    }
  };

  return (
    <div className="stat-card" data-testid={`raid-card-${raid.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-1">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleRename(); if (e.key === "Escape") setEditing(false); }}
                className="h-6 text-sm bg-secondary/50" autoFocus data-testid={`raid-rename-input-${raid.id}`} />
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleRename}>
                <Check className="h-3 w-3 text-green-500" />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setNewName(raid.raid_name); setEditing(false); }}>
                <X className="h-3 w-3 text-muted-foreground" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 group">
              <p className="text-sm font-semibold text-foreground truncate cursor-pointer hover:text-muted-foreground"
                onClick={() => onNavigate(`/negative-overview?raid=${raid.id}`)}>
                {raid.raid_name}
              </p>
              <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                data-testid={`raid-rename-btn-${raid.id}`}>
                <Pencil className="h-3 w-3 text-muted-foreground" />
              </Button>
            </div>
          )}
          {raid.boss_name && <p className="text-xs text-muted-foreground">{raid.boss_name}</p>}
        </div>
        <div className="flex items-center gap-2">
          {raid.kill !== undefined && (
            <Badge variant="outline" className={`text-[10px] ${raid.kill ? "border-green-500/30 text-green-500" : "border-red-500/30 text-red-500"}`}>
              {raid.kill ? "Kill" : "Wipe"}
            </Badge>
          )}
          <Badge variant="outline" className="text-xs font-mono">{raid.date}</Badge>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ stats, raids, team, onNavigate, onDataChanged }) {
  const { activeGuild, isAdmin } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="dashboard-page">
      {/* Guild banner */}
      {activeGuild && (
        <div className="mb-8 relative overflow-hidden rounded-sm border border-purple-500/30 bg-gradient-to-r from-purple-500/10 via-card/40 to-card/20" data-testid="guild-banner">
          <div className="flex items-center gap-4 md:gap-6 p-5">
            {/* Avatar */}
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-sm bg-card/60 border-2 border-purple-500/40 overflow-hidden flex-shrink-0" data-testid="guild-avatar">
              {activeGuild.avatar_url ? (
                <img src={activeGuild.avatar_url} alt={activeGuild.name} className="w-full h-full object-cover object-center" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Shield className="h-10 w-10 text-purple-400/60" />
                </div>
              )}
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h1 className="heading text-3xl md:text-4xl font-bold tracking-tight uppercase truncate" data-testid="guild-name">
                  {activeGuild.name}
                </h1>
                <Badge variant="outline" className="text-[10px] font-mono uppercase border-purple-500/40 text-purple-300">
                  {activeGuild.role}
                </Badge>
              </div>
              {activeGuild.description ? (
                <p className="text-sm text-muted-foreground truncate" data-testid="guild-description">{activeGuild.description}</p>
              ) : (
                <p className="text-xs text-muted-foreground/60 italic">
                  {isAdmin ? "Set a description from guild settings →" : "WoW TBC Classic Anniversary"}
                </p>
              )}
              <div className="flex gap-4 mt-2 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                <span data-testid="guild-stat-players"><span className="text-foreground font-bold">{stats?.total_players ?? "—"}</span> players</span>
                <span data-testid="guild-stat-raids"><span className="text-foreground font-bold">{stats?.total_raids ?? "—"}</span> raids logged</span>
                <span data-testid="guild-stat-team">Currently viewing <span className="text-foreground font-bold">{team}</span></span>
              </div>
            </div>
            {/* Settings button (admin only) */}
            {isAdmin && (
              <Button
                data-testid="guild-settings-btn"
                variant="outline"
                size="sm"
                onClick={() => setSettingsOpen(true)}
                className="text-xs rounded-sm gap-1.5 border-purple-500/40 text-purple-300 hover:bg-purple-500/10 flex-shrink-0"
              >
                <Settings className="h-3 w-3" /> Edit
              </Button>
            )}
          </div>
        </div>
      )}

      {isAdmin && activeGuild && (
        <GuildSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          guild={activeGuild}
          onSaved={onDataChanged}
        />
      )}

      <div className="mb-8">
        <h2 className="heading text-2xl md:text-3xl font-bold tracking-tight uppercase">
          {team} Team
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Raid Performance Dashboard
        </p>
      </div>

      {/* Stats Grid — first 2 cards always, then one card per team */}
      {(() => {
        const teamColors = ["#ABD473", "#F58CBA", "#69CCF0", "#FF7D0A", "#C79C6E", "#9482C9", "#FFF569"];
        const teamRows = Array.isArray(stats?.teams) ? stats.teams : [];
        // Adjust grid columns to fit number of cards
        const totalCards = 2 + teamRows.length;
        const cols = totalCards <= 4 ? "md:grid-cols-4" : totalCards <= 6 ? "md:grid-cols-3" : "md:grid-cols-4";
        return (
          <div className={`grid grid-cols-2 ${cols} gap-4 mb-8`} data-testid="stats-grid">
            <StatCard icon={Users} label="Players" value={stats?.total_players} accent="#69CCF0" />
            <StatCard icon={Calendar} label="Raids Logged" value={stats?.total_raids} accent="#FF7D0A" />
            {teamRows.map((t, i) => (
              <StatCard key={t.team} icon={Swords} label={t.team} value={t.players} accent={teamColors[i % teamColors.length]} />
            ))}
          </div>
        );
      })()}

      {/* Quick Actions */}
      <div className="flex gap-3 mb-8 flex-wrap">
        <Button data-testid="quick-import-btn" onClick={() => onNavigate("/import")}
          className="rounded-sm font-bold uppercase tracking-wider bg-foreground text-background hover:bg-foreground/90 shadow-[0_0_10px_rgba(255,255,255,0.05)]">
          <Upload className="h-4 w-4 mr-2" /> Import RPB
        </Button>
        <Button data-testid="quick-negative-btn" variant="outline" onClick={() => onNavigate("/negative-overview")}
          className="rounded-sm font-bold uppercase tracking-wider border-destructive/50 text-destructive hover:bg-destructive/10">
          <AlertTriangle className="h-4 w-4 mr-2" /> Negative Overview
        </Button>
        <Button data-testid="quick-team-battle-btn" variant="outline" onClick={() => onNavigate("/team-battle")}
          className="rounded-sm font-bold uppercase tracking-wider border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10">
          <Trophy className="h-4 w-4 mr-2" /> Team Battle
        </Button>
      </div>

      {/* Recent Raids */}
      <div>
        <h2 className="heading text-2xl font-semibold tracking-tight mb-4">Recent Raids</h2>
        {raids.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="py-12 text-center">
              <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No raids logged for {team} team yet.</p>
              <p className="text-sm text-muted-foreground mt-1">Import an RPB Excel file to get started.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {raids.slice(0, 9).map((raid) => (
              <RaidCard key={raid.id} raid={raid} onNavigate={onNavigate} onRenamed={onDataChanged} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
